import { DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, MenuProps } from "antd";
import { ReactNode, useMemo } from "react";

export type AddEntityMenuItem = {
  key: string;
  title: ReactNode;
  description: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Right-aligned slot on the title row (e.g. an "AI powered" badge). */
  extra?: ReactNode;
  /** Extra classes for the description line (e.g. "max-w-xs"). */
  descriptionClassName?: string;
};

export type AddEntityLogButtonProps = {
  buttonLabel: string;
  items: AddEntityMenuItem[];
  disabled?: boolean;
  /** Button title/tooltip when disabled (e.g. permission message). */
  disabledTitle?: string;
  /** Button title/tooltip when enabled. Defaults to buttonLabel. */
  enabledTitle?: string;
  /** React key when this button is placed inside a JSX.Element[] list. */
  buttonKey?: string;
  "data-testid"?: string;
};

/**
 * Reusable primary "Add <Entity>" button that opens a hover dropdown of
 * actions (e.g. "Manually create" / "Import"). Driven by an `items` config so
 * it supports 2 or 3 options. Feature-specific nodes (tags, badges) are passed
 * in via `title`/`extra`, keeping this component free of feature imports.
 */
export default function AddEntityLogButton({
  buttonLabel,
  items,
  disabled = false,
  disabledTitle,
  enabledTitle,
  buttonKey,
  "data-testid": dataTestId
}: AddEntityLogButtonProps) {
  const menu: MenuProps = useMemo(
    () => ({
      className: "max-w-sm",
      items: items.map((it) => ({
        key: it.key,
        disabled: it.disabled,
        onClick: it.onClick,
        className: "px-4 py-2",
        label: (
          <div className="flex flex-col space-y-0.5">
            <div className="flex items-start justify-between space-x-3">
              <div className="font-medium text-black text-opacity-90">
                {it.title}
              </div>
              {it.extra && <div className="shrink-0">{it.extra}</div>}
            </div>
            <div
              className={`text-ciq-gray-900 text-xs ${
                it.descriptionClassName ?? ""
              }`}
            >
              {it.description}
            </div>
          </div>
        )
      }))
    }),
    [items]
  );

  return (
    <Dropdown
      key={buttonKey}
      menu={menu}
      trigger={["hover"]}
      disabled={disabled}
    >
      <Button
        type="primary"
        disabled={disabled}
        title={disabled ? disabledTitle : (enabledTitle ?? buttonLabel)}
        data-testid={dataTestId}
      >
        {buttonLabel} <DownOutlined />
      </Button>
    </Dropdown>
  );
}
